import BuoyDeviceModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/BuoyDeviceModal/BuoyDeviceModal';
import FloodStageModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/FloodStageModal/FloodStageModal';
import GenericDeviceModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/GenericDeviceModal/GenericDeviceModal';
import GroupModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/GroupModal/GroupModal';
import USGSModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/USGSModal/USGSModal';
import React, { useEffect } from 'react';

import { Device } from 'features/Integrations/features/ConnectedDevices/types/MonitoringStation_types';
import {
  selectStageOpen,
  selectDeviceSettings,
  selectGroupSettings,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
  selectSelectedGroupId,
  selectUSGSOpen,
} from 'features/Integrations/state/selectors/deviceSelectors';
import {
  clearDeviceSettings,
  clearGroupSettings,
  openDeviceSettings,
  setStageOpen,
  setUSGSOpen,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { BaseDevice } from 'types/devices.types';
import { isBuoyDevice } from '../../../Buoy/utils/buoyDevice.utils';

export default function DeviceSettingsModals(): JSX.Element {
  const dispatch = useDispatch<any>();

  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery(undefined);

  const { data: deviceGroups = [] } = useGetGroupsQuery(undefined);

  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const stageOpen = useSelector(selectStageOpen);
  const usgsOpen = useSelector(selectUSGSOpen);
  const deviceSettings = useSelector(selectDeviceSettings);
  const groupSettings = useSelector(selectGroupSettings);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const selectedGroupId = useSelector(selectSelectedGroupId);

  const explicitDeviceSettingsRequested = deviceSettings != null;

  const potentialDevice: Device | null =
    (connectedDevices as Device[])?.find((device) => device.id === selectedDeviceId) || null;

  const resolvedDeviceSettings: Device | null =
    DeviceIsSystemType(potentialDevice as unknown as BaseDevice, 'Flood') ||
    (potentialDevice as unknown as BaseDevice)?.system_type === 'USGS' ||
    isBuoyDevice(potentialDevice)
      ? null
      : potentialDevice;

  const resolvedBuoySettings =
    isBuoyDevice(potentialDevice) && explicitDeviceSettingsRequested ? potentialDevice : null;

  const resolvedGroupSettings =
    groupSettings || (deviceGroups as any[])?.find((group) => group.id === selectedGroupId) || null;

  const stageDevice =
    (connectedDevices as Device[])?.find((d) => d.id === selectedDeviceId) || null;
  const usgsDevice = (connectedDevices as Device[])?.find((d) => d.id === selectedDeviceId) || null;

  useEffect(() => {
    if (!stageOpen || !selectedDeviceId) return;

    const alreadySelectedId = connectedDevices?.[selectedDeviceIndex]?.id;
    if (alreadySelectedId === selectedDeviceId) return;

    dispatch((setSelectedDevice as any)({ index: -1, id: selectedDeviceId }));
  }, [stageOpen, selectedDeviceId, selectedDeviceIndex, connectedDevices, dispatch]);

  const onSelectFromGroup = (id: number | string) => {
    dispatch((setSelectedDevice as any)({ index: -1, id }));
    const device = (connectedDevices as Device[])?.find((item) => item.id === id);

    if (device && !DeviceIsSystemType(device as unknown as BaseDevice, 'Flood')) {
      dispatch(openDeviceSettings(device.id));
    }
  };

  // Determine which single modal to render. Priority (highest → lowest):
  // USGS modal, Flood (AWARE) modal, Buoy settings, Generic device settings, Group settings
  const selectedModal: 'usgs' | 'flood' | 'buoy' | 'generic' | 'group' | null = (() => {
    if (usgsOpen) return 'usgs';
    if (stageOpen) return 'flood';
    if (resolvedBuoySettings) return 'buoy';
    if (resolvedDeviceSettings) return 'generic';
    if (resolvedGroupSettings) return 'group';
    return null;
  })();

  const modalToRender = (() => {
    switch (selectedModal) {
      case 'usgs':
        return (
          <USGSModal
            usgsOpen={usgsOpen}
            usgsDevice={usgsDevice}
            onSetUSGSOpen={(v: boolean) => dispatch(setUSGSOpen(v))}
          />
        );
      case 'flood':
        return (
          <FloodStageModal
            stageOpen={stageOpen}
            stageDevice={stageDevice}
            onSetStageOpen={(v: boolean) => dispatch(setStageOpen(v))}
          />
        );
      case 'buoy':
        return (
          <BuoyDeviceModal
            device={resolvedBuoySettings}
            stageOpen={stageOpen}
            usgsOpen={usgsOpen}
            onClearDeviceSettings={() => dispatch(clearDeviceSettings())}
          />
        );
      case 'group':
        return (
          <GroupModal
            group={resolvedGroupSettings}
            resolvedDeviceSettings={resolvedDeviceSettings}
            stageOpen={stageOpen}
            usgsOpen={usgsOpen}
            onSelectFromGroup={onSelectFromGroup}
            onClose={() => dispatch(clearGroupSettings())}
          />
        );
      case 'generic':
      default:
        // Generic is the default modal
        return (
          <GenericDeviceModal
            device={resolvedDeviceSettings}
            group={resolvedGroupSettings}
            stageOpen={stageOpen}
            usgsOpen={usgsOpen}
            onClearDeviceSettings={() => dispatch(clearDeviceSettings())}
            onSelectFromGroup={onSelectFromGroup}
          />
        );
    }
  })();

  return <>{modalToRender}</>;
}